import { useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  DragDropContext,
  type DropResult,
} from '@hello-pangea/dnd'
import { addDays, format } from 'date-fns'
import { KanbanColumna } from './KanbanColumna'
import { useTareas, useReordenarTareas, useActualizarTarea, useCambiarEstadoTarea, tareasKeys } from '@/hooks/useTareas'
import type { EstadoTarea, TareaCompleta } from '@/types/database'
import { toast } from 'sonner'
import { clasificarTemporal, type ClasificacionTemporal } from './clasificarTemporal'
import { ordenTrasMover } from './ordenTrasMover'
import { useTareasDeGrupos } from '@/core/hooks/useTareasGrupo'
import { useConfirmarCompletar } from './ConfirmarCompletarDialog'
import {
  agruparTareas,
  type GrupoDeTablero,
  type ItemTablero,
  type TareaConGrupo,
} from '@/core/services/tareasGrupoService'

interface TareasKanbanProps {
  filtroAsignado?: string
  filtroAsignadoPor?: string
  /** La posición "Míos" del interruptor: la tarea asignada a mí. */
  filtroPropias?: string
  filtroCliente?: string
  filtroBusqueda?: string
  mostrarCompletadas?: boolean
  completadasDesde?: string | null
  completadasHasta?: string | null
  onVerDetalle?: (tarea: TareaCompleta) => void
  onEditar?: (tarea: TareaCompleta) => void
  onEliminar?: (tarea: TareaCompleta) => void
  onVerGrupo?: (grupo: GrupoDeTablero) => void
  canEdit?: boolean | ((tarea: TareaCompleta) => boolean)
  canDelete?: boolean
}

interface ColumnaConfig {
  id: string
  titulo: string
}

const COLUMNAS_TEMPORAL: ColumnaConfig[] = [
  { id: 'urgente', titulo: 'Urgente' },
  { id: 'vencida', titulo: 'Vencida' },
  { id: 'hoy', titulo: 'Hoy' },
  { id: 'esta_semana', titulo: 'Esta Semana' },
  { id: 'este_mes', titulo: 'Este Mes' },
  { id: 'sin_fecha', titulo: 'Sin Fecha' },
]

export function TareasKanban({
  filtroAsignado,
  filtroAsignadoPor,
  filtroPropias,
  filtroCliente,
  filtroBusqueda,
  mostrarCompletadas = false,
  completadasDesde,
  completadasHasta,
  onVerDetalle,
  onEditar,
  onEliminar,
  onVerGrupo,
  canEdit = true,
  canDelete = true,
}: TareasKanbanProps) {
  // La columna "Completadas" solo aparece cuando el toggle está activo
  const columnas = useMemo<ColumnaConfig[]>(
    () =>
      mostrarCompletadas
        ? [...COLUMNAS_TEMPORAL, { id: 'completada', titulo: 'Completadas' }]
        : COLUMNAS_TEMPORAL,
    [mostrarCompletadas]
  )

  const { data: tareas = [], isLoading } = useTareas({
    asignado_a: filtroAsignado,
    asignado_por: filtroAsignadoPor,
    propias_de: filtroPropias,
    cliente_id: filtroCliente,
    busqueda: filtroBusqueda,
    mostrarCompletadas,
    completadasDesde: mostrarCompletadas ? completadasDesde : undefined,
    completadasHasta: mostrarCompletadas ? completadasHasta : undefined,
  })

  const queryClient = useQueryClient()
  const reordenar = useReordenarTareas()
  const actualizarTarea = useActualizarTarea()
  const cambiarEstado = useCambiarEstadoTarea()
  const { confirmar, dialogo } = useConfirmarCompletar()

  // Helper: fecha para cada columna temporal
  const fechaParaColumna = (col: ClasificacionTemporal): string | null => {
    const hoy = new Date()
    switch (col) {
      case 'hoy': return format(hoy, 'yyyy-MM-dd')
      case 'esta_semana': return format(addDays(hoy, 3), 'yyyy-MM-dd')
      case 'este_mes': return format(addDays(hoy, 15), 'yyyy-MM-dd')
      default: return null
    }
  }

  // El tablero no trae las completadas. El avance de un grupo las cuenta, así que sus tareas se
  // piden aparte.
  const clavesDeGrupo = useMemo(
    () => (tareas as TareaConGrupo[]).flatMap((t) => (t.grupo_clave ? [t.grupo_clave] : [])),
    [tareas]
  )
  const { data: tareasDeGrupos = [] } = useTareasDeGrupos(clavesDeGrupo)

  const itemsPorColumna = useMemo(() => {
    const grouped: Record<string, ItemTablero[]> = {}
    columnas.forEach((col) => { grouped[col.id] = [] })

    // Server-side filtering handles completadas — no client-side filter needed
    const porPosicion = [...tareas].sort((a, b) => (a.posicion ?? 0) - (b.posicion ?? 0))
    agruparTareas(porPosicion, tareasDeGrupos, (t) => clasificarTemporal(t), columnas.map((c) => c.id))
      .forEach((item) => grouped[item.columna]?.push(item))

    return grouped
  }, [tareas, tareasDeGrupos, columnas])

  const handleDragEnd = (result: DropResult) => {
    const { source, destination, draggableId } = result

    // ORR-200 B3: con canEdit por fila, solo se arrastra la tarea editable
    const tareaArrastrada = tareas.find((t) => t.id === draggableId)
    const canEditArrastrada =
      typeof canEdit === 'function' ? !!tareaArrastrada && canEdit(tareaArrastrada) : canEdit
    if (!canEditArrastrada) return
    if (!destination) return
    if (source.droppableId === destination.droppableId && source.index === destination.index) return

    const tareaId = draggableId

    // Drag entre columnas temporales: cambia prioridad/fecha
    if (source.droppableId !== destination.droppableId) {
      const destino = destination.droppableId as ClasificacionTemporal

      // Mover a "Completadas" → marcar la tarea como completada
      if (destino === 'completada') {
        const tarea = tareas.find((t) => t.id === tareaId)
        confirmar('completada', () =>
          cambiarEstado.mutate(
            { id: tareaId, estado: 'completada', estadoAnterior: tarea?.estado as EstadoTarea | undefined },
            {
              onSuccess: () => toast.success('Tarea completada'),
              onError: () => toast.error('Error al completar la tarea'),
            }
          )
        )
        return
      }

      // Sacar de "Completadas" hacia una columna temporal → reabrir (pendiente)
      if (source.droppableId === 'completada') {
        cambiarEstado.mutate(
          { id: tareaId, estado: 'pendiente', estadoAnterior: 'completada' },
          {
            onSuccess: () => toast.success('Tarea reabierta'),
            onError: () => toast.error('Error al reabrir la tarea'),
          }
        )
        return
      }

      if (destino === 'vencida') {
        toast.info('No se puede mover a Vencida')
        return
      }

      const datos: Record<string, unknown> = {}
      let mensaje = ''

      if (destino === 'urgente') {
        datos.prioridad = 'urgente'
        mensaje = 'Marcada como urgente'
      } else if (destino === 'sin_fecha') {
        datos.fecha_limite = null
        if (source.droppableId === 'urgente') datos.prioridad = 'media'
        mensaje = 'Fecha límite eliminada'
      } else {
        const nuevaFecha = fechaParaColumna(destino)
        datos.fecha_limite = nuevaFecha
        if (source.droppableId === 'urgente') datos.prioridad = 'media'
        const labels: Record<string, string> = { hoy: 'hoy', esta_semana: 'en 3 días', este_mes: 'en 15 días' }
        mensaje = `Fecha límite: ${labels[destino] ?? destino}`
      }

      queryClient.setQueriesData({ queryKey: tareasKeys.all }, (old: unknown) =>
        Array.isArray(old) ? old.map((t: TareaCompleta) => t.id === tareaId ? { ...t, ...datos } : t) : old
      )

      actualizarTarea.mutate(
        { id: tareaId, datos },
        {
          onSuccess: () => toast.success(mensaje),
          onError: () => {
            queryClient.invalidateQueries({ queryKey: tareasKeys.all })
            toast.error('Error al actualizar la tarea')
          },
        }
      )
    } else if (source.index !== destination.index) {
      const orden = ordenTrasMover(itemsPorColumna[source.droppableId] ?? [], source.index, destination.index)
      reordenar.mutate(
        { tareaId, orden },
        { onError: () => toast.error('Error al reordenar') }
      )
    }
  }

  if (isLoading) {
    return (
      <div className="flex gap-4 h-[calc(100vh-250px)] animate-pulse">
        {columnas.map((col) => (
          <div key={col.id} className="flex-1 min-w-[300px] max-w-[350px] bg-muted rounded-lg" />
        ))}
      </div>
    )
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      {dialogo}
      <div className="flex gap-4 h-[calc(100vh-250px)] pb-4 overflow-x-auto">
        {columnas.map((columna) => (
          <KanbanColumna
            key={columna.id}
            id={columna.id}
            titulo={columna.titulo}
            items={itemsPorColumna[columna.id] ?? []}
            colorScheme="temporal"
            onVerDetalle={onVerDetalle}
            onEditar={onEditar}
            onEliminar={onEliminar}
            onVerGrupo={onVerGrupo}
            canEdit={canEdit}
            canDelete={canDelete}
          />
        ))}
      </div>
    </DragDropContext>
  )
}
